import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { digiStoreApi, getMediaUrl } from '../services/api';

function DigiStorePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFormat, setSelectedFormat] = useState('All');
  const [selectedPriceFilter, setSelectedPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  
  // Available filter options
  const [categories, setCategories] = useState(['All']);
  const [formats, setFormats] = useState(['All']);
  
  // Preview Modal
  const [previewProduct, setPreviewProduct] = useState(null);
  
  // Notification Toast
  const [toastMessage, setToastMessage] = useState('');
  const [cartCount, setCartCount] = useState(0);

  // FAQ open states
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchProducts();
    updateCartCount();
    // Initialize WOW.js if available
    setTimeout(() => {
      if (window.WOW) {
        try { new window.WOW().init(); } catch (e) {}
      }
    }, 400);
  }, []);

  const updateCartCount = () => {
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const count = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
      setCartCount(count);
    } catch (e) {
      setCartCount(0);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await digiStoreApi.getProducts({ limit: 50, sortBy: 'popular' });
      if (res && res.success && Array.isArray(res.products)) {
        setProducts(res.products);
        if (res.categories && res.categories.length > 0) {
          setCategories(res.categories);
        } else {
          const uniqueCats = ['All', ...new Set(res.products.map(p => p.category).filter(Boolean))];
          setCategories(uniqueCats);
        }
        if (res.formats && res.formats.length > 0) {
          setFormats(res.formats);
        } else {
          const uniqueFormats = ['All', ...new Set(res.products.map(p => p.format).filter(Boolean))];
          setFormats(uniqueFormats);
        }
      } else {
        throw new Error('Could not retrieve products');
      }
    } catch (err) {
      console.warn('API error, using fallback:', err.message);
      setError('Could not connect to online catalog. Displaying offline catalog.');
    } finally {
      setLoading(false);
    }
  };

  // Filtered and Sorted products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = product.title?.toLowerCase().includes(q);
        const matchesDesc = product.description?.toLowerCase().includes(q);
        const matchesCat = product.category?.toLowerCase().includes(q);
        const matchesTags = Array.isArray(product.tags) && product.tags.some(t => t.toLowerCase().includes(q));
        if (!matchesTitle && !matchesDesc && !matchesCat && !matchesTags) return false;
      }

      // Category
      if (selectedCategory !== 'All' && product.category?.toLowerCase() !== selectedCategory.toLowerCase()) {
        return false;
      }

      // Format
      if (selectedFormat !== 'All' && product.format?.toLowerCase() !== selectedFormat.toLowerCase()) {
        return false;
      }

      // Price filter
      if (selectedPriceFilter === 'free' && product.price > 0) return false;
      if (selectedPriceFilter === 'under-300' && (product.price === 0 || product.price > 300)) return false;
      if (selectedPriceFilter === '300-500' && (product.price <= 300 || product.price > 500)) return false;
      if (selectedPriceFilter === 'above-500' && product.price <= 500) return false;

      return true;
    }).sort((a, b) => {
      if (sortBy === 'popular') return (b.downloadCount || 0) - (a.downloadCount || 0);
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'price-asc') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price-desc') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'newest') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      return 0;
    });
  }, [products, searchQuery, selectedCategory, selectedFormat, selectedPriceFilter, sortBy]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage('');
    }, 3500);
  };

  const handleAddToCart = (product, e) => {
    if (e) e.stopPropagation();

    // If free product, trigger direct download or instant access
    if (product.price === 0) {
      if (product.fileUrl) {
        window.open(product.fileUrl, '_blank');
      }
      showToast(`🎉 Free Download started for "${product.title}"!`);
      return;
    }

    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const productId = product._id || product.id;
      const existingIndex = cart.findIndex(item => (item._id || item.id) === productId);

      if (existingIndex > -1) {
        cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
      } else {
        cart.push({
          id: productId,
          _id: productId,
          name: product.title,
          title: product.title,
          price: product.price,
          originalPrice: product.originalPrice,
          image: product.thumbnail || '/assets/images/services_new/digistore.jpeg',
          category: product.category,
          format: product.format,
          quantity: 1,
        });
      }

      localStorage.setItem('cart', JSON.stringify(cart));
      updateCartCount();
      showToast(`✓ "${product.title}" added to your Cart!`);
    } catch (e) {
      console.error(e);
      alert('Added to cart!');
    }
  };

  const handleInstantBuy = (product) => {
    handleAddToCart(product);
    navigate('/cart');
  };

  const faqs = [
    {
      q: 'How do I access my purchased digital resources?',
      a: 'Immediately upon checkout completion, you will receive an instant on-screen download link as well as a confirmation email containing your lifetime download access credentials for all files (PDF, Audio, or Templates).'
    },
    {
      q: 'Can mental health practitioners use these worksheets with clients?',
      a: 'Yes, absolutely! All PsychMitra clinical worksheets, intake assessment forms, and CBT/DBT protocols come with practitioner clinical licenses permitting individual printing and therapeutic sharing with your private practice clients.'
    },
    {
      q: 'Are the clinical worksheets fillable on phones, tablets, and computers?',
      a: 'Yes! Our digital workbooks and worksheets are built with interactive form fields. You or your clients can fill them out directly on laptops, iPads, tablets, or smartphones without needing expensive paid PDF editors.'
    },
    {
      q: 'Are updates included if a worksheet or guide is revised?',
      a: 'Yes, PsychMitra DigiStore provides lifetime access. Whenever our clinical psychology committee updates a clinical protocol or guide, you can re-download the latest updated version at no additional cost.'
    },
    {
      q: 'What payment modes are supported?',
      a: 'We support all major Indian and international payment methods including UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, Net Banking, and secure Razorpay payment gateways.'
    }
  ];

  return (
    <div className="digistore-page-wrapper" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          backgroundColor: '#0f172a',
          color: '#ffffff',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '15px',
          fontWeight: 600,
          border: '1px solid rgba(255,255,255,0.1)',
          animation: 'slideUp 0.3s ease-out'
        }}>
          <span style={{ color: '#10b981', fontSize: '20px' }}>✓</span>
          <span>{toastMessage}</span>
          <Link
            to="/cart"
            style={{
              marginLeft: '10px',
              backgroundColor: '#4f46e5',
              color: '#fff',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '13px',
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            View Cart
          </Link>
        </div>
      )}

      {/* Floating Cart Button for Quick Access */}
      <Link
        to="/cart"
        style={{
          position: 'fixed',
          bottom: '30px',
          left: '30px',
          backgroundColor: '#4f46e5',
          color: '#ffffff',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 10px 20px rgba(79, 70, 229, 0.4)',
          zIndex: 9999,
          textDecoration: 'none',
          transition: 'transform 0.2s',
          fontSize: '22px'
        }}
        title="View Shopping Cart"
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        🛒
        {cartCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            backgroundColor: '#ef4444',
            color: 'white',
            borderRadius: '50%',
            width: '22px',
            height: '22px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            border: '2px solid white'
          }}>
            {cartCount}
          </span>
        )}
      </Link>

      {/* Hero Section */}
      <section style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #1e293b 100%)',
        color: '#ffffff',
        padding: '100px 0 70px 0',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, rgba(99, 102, 241, 0) 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-15%',
          left: '-5%',
          width: '450px',
          height: '450px',
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.18) 0%, rgba(236, 72, 153, 0) 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(8px)',
                padding: '6px 16px',
                borderRadius: '50px',
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.5px',
                marginBottom: '20px',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                <span style={{ color: '#fbbf24' }}>✨</span>
                <span>PSYCHMITRA CLINICAL DIGITAL STORE</span>
              </div>

              <h1 style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.4rem)',
                fontWeight: 800,
                lineHeight: 1.15,
                marginBottom: '20px',
                letterSpacing: '-0.5px'
              }}>
                Therapeutic Worksheets, Guides & <span style={{
                  background: 'linear-gradient(90deg, #a5b4fc, #f472b6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>Evidence-Based Tools</span>
              </h1>

              <p style={{
                fontSize: '17px',
                lineHeight: 1.6,
                color: '#cbd5e1',
                maxWidth: '680px',
                marginBottom: '30px'
              }}>
                Carefully crafted by licensed clinical psychologists and counselors. Empower your personal self-healing journey or equip your private therapy practice with standardized clinical interventions.
              </p>

              {/* Quick Search in Hero */}
              <div style={{
                display: 'flex',
                maxWidth: '560px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '6px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4)'
              }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search CBT thought records, anxiety guides, sleep checklists..."
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    padding: '12px 18px',
                    fontSize: '15px',
                    color: '#1e293b',
                    borderRadius: '8px'
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '0 12px',
                      fontSize: '16px'
                    }}
                  >
                    ✕
                  </button>
                )}
                <button
                  type="button"
                  style={{
                    backgroundColor: '#4f46e5',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0 24px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <i className="fas fa-search"></i>
                  <span>Search</span>
                </button>
              </div>
            </div>

            <div className="col-lg-4 text-center mt-5 mt-lg-0">
              <div style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(12px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                padding: '30px',
                textAlign: 'left'
              }}>
                <h4 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>
                  🎯 Why PsychMitra DigiStore?
                </h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#e2e8f0' }}>
                    <span style={{ background: '#10b981', color: '#fff', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>✓</span>
                    <span>100% Clinically Validated Interventions</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#e2e8f0' }}>
                    <span style={{ background: '#10b981', color: '#fff', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>✓</span>
                    <span>Fillable Forms + High-Res Print-Ready (300 DPI)</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#e2e8f0' }}>
                    <span style={{ background: '#10b981', color: '#fff', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>✓</span>
                    <span>Instant Lifetime Access & Free Revisions</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#e2e8f0' }}>
                    <span style={{ background: '#10b981', color: '#fff', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>✓</span>
                    <span>Practitioner Client-Distribution License</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Bar */}
      <section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0', padding: '18px 0' }}>
        <div className="container">
          <div className="row text-center" style={{ rowGap: '12px' }}>
            <div className="col-6 col-md-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🛡️</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>RCI-Licensed Clinicians</span>
            </div>
            <div className="col-6 col-md-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>⚡</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Instant Download (PDF/Audio)</span>
            </div>
            <div className="col-6 col-md-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>🔒</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>100% Confidential & Secure</span>
            </div>
            <div className="col-6 col-md-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>⭐</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>Rated 4.9/5 by 5,000+ Users</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Catalog Section */}
      <section style={{ padding: '50px 0 80px 0' }}>
        <div className="container">

          {/* Category Filter Pills */}
          <div style={{
            display: 'flex',
            gap: '10px',
            overflowX: 'auto',
            paddingBottom: '16px',
            marginBottom: '30px',
            scrollbarWidth: 'none'
          }}>
            {categories.map((cat) => {
              const isActive = selectedCategory.toLowerCase() === cat.toLowerCase();
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    backgroundColor: isActive ? '#4f46e5' : '#ffffff',
                    color: isActive ? '#ffffff' : '#475569',
                    border: isActive ? '1px solid #4f46e5' : '1px solid #e2e8f0',
                    padding: '10px 22px',
                    borderRadius: '50px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    boxShadow: isActive ? '0 4px 12px rgba(79, 70, 229, 0.25)' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {cat === 'All' && '🌟 '}
                  {cat === 'Worksheets & Toolkits' && '📑 '}
                  {cat === 'E-Books & Manuals' && '📖 '}
                  {cat === 'Audio & Meditations' && '🎧 '}
                  {cat === 'Clinical Templates' && '📋 '}
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Sub-Filters and Sort Controls */}
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '18px 24px',
            marginBottom: '35px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid #edf2f7',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                Showing <span style={{ color: '#0f172a', fontWeight: 700 }}>{filteredProducts.length}</span> tools
              </div>

              {/* Price Filter Pill */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Price:</span>
                <select
                  value={selectedPriceFilter}
                  onChange={(e) => setSelectedPriceFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    backgroundColor: '#fff',
                    color: '#334155',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Prices</option>
                  <option value="free">100% Free</option>
                  <option value="under-300">Under ₹300</option>
                  <option value="300-500">₹300 - ₹500</option>
                  <option value="above-500">Above ₹500</option>
                </select>
              </div>

              {/* Format Filter */}
              {formats.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>Format:</span>
                  <select
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '13px',
                      backgroundColor: '#fff',
                      color: '#334155',
                      cursor: 'pointer'
                    }}
                  >
                    {formats.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Sort By:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                  fontWeight: 500,
                  backgroundColor: '#fff',
                  color: '#334155',
                  cursor: 'pointer'
                }}
              >
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="newest">Newest Releases</option>
              </select>
            </div>
          </div>

          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              padding: '12px 18px',
              borderRadius: '10px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: 600
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 20px' }}>
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
              <p style={{ marginTop: '16px', color: '#64748b', fontSize: '16px' }}>Loading PsychMitra DigiStore catalog...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="row g-4">
              {filteredProducts.map((product) => {
                const discount = product.originalPrice && product.originalPrice > product.price
                  ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
                  : 0;

                return (
                  <div key={product._id || product.id} className="col-lg-4 col-md-6">
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-6px)';
                        e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)';
                      }}
                    >
                      {/* Image Thumbnail with Format Badge */}
                      <div
                        style={{
                          height: '210px',
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundColor: '#e2e8f0',
                          cursor: 'pointer'
                        }}
                        onClick={() => setPreviewProduct(product)}
                      >
                        <img
                          src={product.thumbnail ? getMediaUrl(product.thumbnail) : '/assets/images/services_new/digistore.jpeg'}
                          alt={product.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.4s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        />

                        {/* Badges Overlay */}
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          left: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}>
                          {product.format && (
                            <span style={{
                              backgroundColor: 'rgba(15, 23, 42, 0.85)',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 700,
                              padding: '4px 10px',
                              borderRadius: '6px',
                              backdropFilter: 'blur(4px)',
                              letterSpacing: '0.3px'
                            }}>
                              {product.format}
                            </span>
                          )}
                          {product.isFeatured && (
                            <span style={{
                              backgroundColor: '#d97706',
                              color: '#fff',
                              fontSize: '11px',
                              fontWeight: 700,
                              padding: '4px 8px',
                              borderRadius: '6px'
                            }}>
                              ⭐ Top Pick
                            </span>
                          )}
                        </div>

                        {discount > 0 && product.price > 0 && (
                          <span style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            backgroundColor: '#ef4444',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 800,
                            padding: '4px 10px',
                            borderRadius: '50px',
                            boxShadow: '0 4px 6px rgba(239, 68, 68, 0.4)'
                          }}>
                            {discount}% OFF
                          </span>
                        )}

                        {product.price === 0 && (
                          <span style={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            backgroundColor: '#10b981',
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: 800,
                            padding: '4px 12px',
                            borderRadius: '50px',
                            boxShadow: '0 4px 6px rgba(16, 185, 129, 0.4)'
                          }}>
                            100% FREE
                          </span>
                        )}

                        {/* Quick View Button overlay on hover */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewProduct(product);
                          }}
                          style={{
                            position: 'absolute',
                            bottom: '12px',
                            right: '12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            color: '#1e293b',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                          }}
                        >
                          👁️ Quick View
                        </button>
                      </div>

                      {/* Product Content Body */}
                      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6366f1',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {product.category || 'Clinical Tool'}
                          </span>
                          
                          {/* Rating */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                            <span style={{ color: '#f59e0b' }}>★</span>
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{product.rating || 4.8}</span>
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>({product.ratingCount || 12})</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h3
                          onClick={() => setPreviewProduct(product)}
                          style={{
                            fontSize: '17px',
                            fontWeight: 700,
                            color: '#0f172a',
                            lineHeight: 1.35,
                            marginBottom: '10px',
                            cursor: 'pointer',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {product.title}
                        </h3>

                        {/* Snippet Description */}
                        <p style={{
                          fontSize: '13px',
                          color: '#64748b',
                          lineHeight: 1.5,
                          marginBottom: '14px',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {product.description}
                        </p>

                        {/* Meta Tags (Pages, Target Audience) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
                          {product.pages && (
                            <span style={{
                              fontSize: '11px',
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              fontWeight: 500
                            }}>
                              📄 {product.pages}
                            </span>
                          )}
                          <span style={{
                            fontSize: '11px',
                            backgroundColor: '#ecfdf5',
                            color: '#065f46',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontWeight: 500
                          }}>
                            ✓ Clinician Verified
                          </span>
                        </div>

                        {/* Footer Price & Add To Cart Button */}
                        <div style={{
                          marginTop: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: '1px solid #f1f5f9',
                          paddingTop: '16px'
                        }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                              <span style={{
                                fontSize: '20px',
                                fontWeight: 800,
                                color: product.price === 0 ? '#10b981' : '#0f172a'
                              }}>
                                {product.price === 0 ? 'FREE' : `₹${product.price}`}
                              </span>
                              {product.originalPrice > product.price && (
                                <span style={{
                                  fontSize: '13px',
                                  color: '#94a3b8',
                                  textDecoration: 'line-through'
                                }}>
                                  ₹{product.originalPrice}
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              {product.price === 0 ? 'Direct Download' : 'Instant Lifetime Access'}
                            </span>
                          </div>

                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              onClick={(e) => handleAddToCart(product, e)}
                              style={{
                                backgroundColor: product.price === 0 ? '#10b981' : '#4f46e5',
                                color: '#ffffff',
                                border: 'none',
                                padding: '10px 16px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'background-color 0.2s ease, transform 0.1s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.opacity = 0.9}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
                            >
                              {product.price === 0 ? (
                                <>
                                  <span>⬇️</span> Download
                                </>
                              ) : (
                                <>
                                  <span>🛒</span> Add to Cart
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '70px 20px',
              backgroundColor: '#fff',
              borderRadius: '16px',
              border: '1px solid #e2e8f0'
            }}>
              <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🔍</span>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                No tools found matching your criteria
              </h3>
              <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '400px', margin: '0 auto 20px auto' }}>
                Try adjusting your search terms or clearing your category & price filters.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('All');
                  setSelectedFormat('All');
                  setSelectedPriceFilter('all');
                }}
                style={{
                  backgroundColor: '#4f46e5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Reset All Filters
              </button>
            </div>
          )}

        </div>
      </section>

      {/* Practitioner Creator Callout Banner */}
      <section style={{
        background: 'linear-gradient(135deg, #312e81 0%, #1e1b4b 100%)',
        color: '#fff',
        padding: '60px 0',
        marginBottom: '60px'
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.15)',
                color: '#facc15',
                fontSize: '12px',
                fontWeight: 700,
                padding: '4px 12px',
                borderRadius: '50px',
                letterSpacing: '0.5px'
              }}>
                FOR PSYCHOLOGISTS, THERAPISTS & EDUCATORS
              </span>
              <h2 style={{ fontSize: '28px', fontWeight: 800, marginTop: '14px', marginBottom: '10px' }}>
                Publish Your Clinical Tools on PsychMitra DigiStore
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: '15px', maxWidth: '650px', margin: 0 }}>
                Are you a mental health professional with evidence-based worksheets, manuals, or guided audio? Join our verified practitioner creator network and earn revenue while helping individuals heal across India.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end mt-4 mt-lg-0">
              <Link
                to="/register/physiotherapist"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#312e81',
                  padding: '14px 28px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '15px',
                  textDecoration: 'none',
                  display: 'inline-block',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.2)'
                }}
              >
                Join as Practitioner →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '20px 0 80px 0' }}>
        <div className="container" style={{ maxWidth: '850px' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <span style={{ color: '#4f46e5', fontWeight: 700, fontSize: '13px', letterSpacing: '1px' }}>
              FREQUENTLY ASKED QUESTIONS
            </span>
            <h2 style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>
              DigiStore Questions & Answers
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    style={{
                      width: '100%',
                      padding: '18px 24px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: 'none',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      {faq.q}
                    </span>
                    <span style={{ fontSize: '18px', color: '#6366f1', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                      ▼
                    </span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: '0 24px 20px 24px', fontSize: '14px', color: '#475569', lineHeight: 1.6 }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick View / Detail Modal */}
      {previewProduct && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(6px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setPreviewProduct(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              maxWidth: '850px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setPreviewProduct(null)}
              style={{
                position: 'absolute',
                top: '18px',
                right: '18px',
                backgroundColor: '#f1f5f9',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#475569',
                zIndex: 10
              }}
            >
              ✕
            </button>

            <div className="row g-0">
              {/* Product Cover Column */}
              <div className="col-md-5" style={{ backgroundColor: '#f8fafc', padding: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: '100%',
                  height: '320px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
                }}>
                  <img
                    src={previewProduct.thumbnail ? getMediaUrl(previewProduct.thumbnail) : '/assets/images/services_new/digistore.jpeg'}
                    alt={previewProduct.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>

                <div style={{ marginTop: '20px', textAlign: 'center', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a' }}>
                      {previewProduct.price === 0 ? 'FREE' : `₹${previewProduct.price}`}
                    </span>
                    {previewProduct.originalPrice > previewProduct.price && (
                      <span style={{ fontSize: '16px', color: '#94a3b8', textDecoration: 'line-through' }}>
                        ₹{previewProduct.originalPrice}
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: '#10b981', fontWeight: 600, margin: '4px 0 0 0' }}>
                    ✓ Instant Digital Delivery (No shipping delay)
                  </p>
                </div>
              </div>

              {/* Product Info Column */}
              <div className="col-md-7" style={{ padding: '32px' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#4f46e5',
                    backgroundColor: '#eef2ff',
                    padding: '4px 10px',
                    borderRadius: '50px'
                  }}>
                    {previewProduct.category}
                  </span>
                  {previewProduct.format && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#0f172a',
                      backgroundColor: '#f1f5f9',
                      padding: '4px 10px',
                      borderRadius: '50px'
                    }}>
                      {previewProduct.format}
                    </span>
                  )}
                </div>

                <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '12px', lineHeight: 1.3 }}>
                  {previewProduct.title}
                </h2>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <div style={{ color: '#f59e0b', fontSize: '14px' }}>★ ★ ★ ★ ★</div>
                  <span style={{ fontSize: '13px', fontWeight: 700 }}>{previewProduct.rating || 4.9}</span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>({previewProduct.ratingCount || 15} verified ratings)</span>
                </div>

                <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '20px' }}>
                  {previewProduct.description}
                </p>

                {/* Key Features List */}
                {Array.isArray(previewProduct.features) && previewProduct.features.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h5 style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', marginBottom: '10px' }}>
                      What's Included:
                    </h5>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {previewProduct.features.map((feat, i) => (
                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#334155' }}>
                          <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {previewProduct.targetAudience && (
                  <div style={{ marginBottom: '24px', fontSize: '13px', color: '#64748b' }}>
                    <strong>Recommended for:</strong> {previewProduct.targetAudience}
                  </div>
                )}

                {/* Modal Buttons */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      handleAddToCart(previewProduct);
                      setPreviewProduct(null);
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: '#4f46e5',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px 20px',
                      fontSize: '15px',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {previewProduct.price === 0 ? '⬇️ Download Free Now' : '🛒 Add to Cart'}
                  </button>

                  {previewProduct.price > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        handleInstantBuy(previewProduct);
                        setPreviewProduct(null);
                      }}
                      style={{
                        backgroundColor: '#10b981',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '14px 22px',
                        fontSize: '15px',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      ⚡ Buy Now
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default DigiStorePage;
